import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import * as Client from 'api/client';
import { useLiff } from 'providers/Liff';
import { useToast } from 'providers/Toast';
import Context from './Context';

import { lineOAUrl, stage } from 'config/const';
import { getMobileNumber, isMobile } from 'utils/method';
import { getClientProfiles } from './method';

let refreshingClient = false;
const Provider = ({ children }) => {
  const [auth, setAuth] = useState({});
  const [client, setClient] = useState({});
  const [customerInfo, setCustomerInfo] = useState({});
  const [profile, setProfile] = useState({});
  const [initFinish, setInitFinish] = useState(false);
  const { liff, ready } = useLiff();
  const { danger } = useToast();
  const intl = useIntl();

  const validAuth =
    auth.userId && auth.accessToken && !isEmpty(profile) && !isEmpty(client);
  const refreshToken = get(client, 'idToken.refreshToken');

  const refreshClient = async () => {
    if (refreshingClient) return;
    refreshingClient = true;
    const { client, profile, accessToken, userId, error } =
      await getClientProfiles(liff);
    refreshingClient = false;

    if (isEmpty(client) || isEmpty(profile) || error) {
      danger(intl.formatMessage({ id: 'get_customer_error_msg' }));
      return { err: true };
    }

    client.phone = (client.phone || '').slice(1);
    const { firstName = '', lastName = '', name = '', fullname = '' } = client;
    if (!firstName && !lastName) client.firstName = name;
    if (fullname) client.name = fullname.replace(' ', '');

    setProfile(profile);
    setClient(client);
    setAuth({ id: client.id, accessToken, userId, idToken: client.idToken });
    return { err: false };
  };

  const getCustomerInfo = () =>
    Client.getCustomerDetail(auth.userId).then((res) => {
      if (!res.err) {
        const myProfile = get(res, 'data.myProfile', {});
        const [year, month, day] = get(myProfile, 'birthday', '').split('/');
        const { mobile } = myProfile;

        setCustomerInfo({
          ...res.data,
          myProfile: {
            ...myProfile,
            month,
            day,
            year,
            mobile: (mobile || '').slice(1),
          },
        });
      }
      return res.err ? false : res;
    });

  const saveClient = (data) => {
    return Client.createCustomer(
      { ...data, mobile: getMobileNumber(data.mobile) },
      auth.accessToken,
    ).then((res) => {
      if (res.err)
        danger(intl.formatMessage({ id: 'save_customer_error_msg' }));
      return res;
    });
  };

  const updateClient = (details) => {
    const {
      companyName,
      email,
      occupation,
      city,
      address,
      mobile,
      birthday,
      firstName,
      lastName,
      caseName,
      sourceSales,
    } = details;

    const data = {
      companyName,
      email,
      occupation,
      city,
      address,
      mobile: getMobileNumber(mobile),
      birthday,
      firstName,
      lastName,
      caseName,
      sourceSales,
    };

    return Client.updateClientDetail(auth.userId, auth.accessToken, data).then(
      (res) => {
        if (res.err)
          danger(intl.formatMessage({ id: 'update_customer_error_msg' }));
        else {
          refreshClient();
          setCustomerInfo(res.data);
        }
        return !res.err ? res.data : false;
      },
    );
  };

  const updateClientAgent = (agentId) =>
    Client.updateClientAgent(auth.userId, agentId).then((res) => {
      if (res.err) danger(intl.formatMessage({ id: 'bind_error' }));
      return !res.err ? res : false;
    });

  const updateTpClient = (data) =>
    Client.updateTpCustomer(auth.userId, auth.idToken, data).then((res) => {
      if (res.err)
        danger(intl.formatMessage({ id: 'update_customer_error_msg' }));
      else refreshClient();
      return !res.err ? res : false;
    });

  const refresh = useCallback(async () => {
    if (!refreshToken) return;
    const newIdToken = await Client.refreshIdToken(refreshToken);
    if (newIdToken.err) {
      console.error(newIdToken.err);
      return;
    }
    setAuth({ ...auth, idToken: newIdToken });
  }, [refreshToken]);

  useEffect(() => {
    if (!liff || !ready) return;

    if (stage !== 'local' && !isMobile())
      return window.location.replace(lineOAUrl);

    if (!liff.isLoggedIn())
      return liff.login({ redirectUri: window.location.href });
    refreshClient().then(() => {
      setInitFinish(true);
    });
  }, [liff, ready]);

  const props = {
    auth,
    profile,
    client,
    validAuth,
    customerInfo,
    refresh,
    refreshClient,
    updateClient,
    updateTpClient,
    saveClient,
    getCustomerInfo,
    updateClientAgent,
    initFinish,
  };

  return <Context.Provider value={{ ...props }}>{children}</Context.Provider>;
};

export default Provider;
